import { useEffect, useMemo, useState, type FormEvent } from "react";

import { Button, Checkbox, SelectField, TextField } from "../../../components/ui/Controls";
import { AlertBanner, InlineResult } from "../../../components/ui/Feedback";
import { apiErrorMessage } from "../../../lib/auth";
import { projectDraftsApi, type ProjectDraftInput, type SafeProjectDraft } from "../../../lib/api";
import { WizardReview } from "./WizardReview";

const steps = [
  "Kaynak",
  "Framework",
  "Çalışma zamanı",
  "Sınırlar ve ağ",
  "Gizli değer adları",
  "Veritabanı",
  "Alan adı ve HTTPS",
  "İnceleme",
] as const;

export function ProjectWizard({ initialDraftId }: { readonly initialDraftId?: string }) {
  const [draft, setDraft] = useState<SafeProjectDraft>();
  const [resumeId, setResumeId] = useState(initialDraftId ?? "");
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [projectId, setProjectId] = useState("");
  const [sourceConnectionId, setSourceConnectionId] = useState("");
  const [framework, setFramework] = useState<ProjectDraftInput["framework"]>("node");
  const [runtime, setRuntime] = useState<"docker" | "native">("docker");
  const [trustedNative, setTrustedNative] = useState(false);
  const [healthPath, setHealthPath] = useState("/health");
  const [environmentName, setEnvironmentName] = useState("");
  const [environmentValue, setEnvironmentValue] = useState("");
  const [databaseId, setDatabaseId] = useState("");
  const [domainId, setDomainId] = useState("");

  const input = useMemo<ProjectDraftInput>(
    () => ({
      projectId: projectId.trim(),
      sourceConnectionId: sourceConnectionId.trim(),
      framework,
      runtime,
      ...(runtime === "native" && trustedNative
        ? { trustedNativeAcknowledgement: true as const }
        : {}),
      limits: { cpuMillicores: 500, memoryMiB: 512, diskMiB: 2048 },
      network: { egress: "blocked" },
      healthPath: healthPath.trim(),
      ...(environmentName.trim() === ""
        ? {}
        : { environment: [{ name: environmentName.trim(), value: environmentValue }] }),
      ...(databaseId.trim() === "" ? {} : { databaseId: databaseId.trim() }),
      ...(domainId.trim() === "" ? {} : { domainId: domainId.trim() }),
    }),
    [
      projectId,
      sourceConnectionId,
      framework,
      runtime,
      trustedNative,
      healthPath,
      environmentName,
      environmentValue,
      databaseId,
      domainId,
    ],
  );

  useEffect(() => {
    if (draft !== undefined) sessionStorage.setItem("webyonetim:last-project-draft", draft.id);
  }, [draft]);
  const resume = async () => {
    if (resumeId.trim() === "") return;
    setBusy(true);
    setError(undefined);
    try {
      const value = await projectDraftsApi.get(resumeId.trim());
      setDraft(value);
      setProjectId(value.projectId);
      setSourceConnectionId(value.sourceConnectionId);
      setFramework(value.effectiveConfig.framework);
      setRuntime(value.effectiveConfig.runtime);
      setTrustedNative(value.effectiveConfig.runtime === "native");
      setHealthPath(value.effectiveConfig.healthPath);
      setDatabaseId(value.effectiveConfig.databaseId ?? "");
      setDomainId(value.effectiveConfig.domainId ?? "");
      setStep(steps.length - 1);
    } catch (caught) {
      setError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (runtime === "native" && !trustedNative) {
      setError("Yerel çalışma zamanı için açık güven onayı gerekir.");
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const value =
        draft === undefined
          ? await projectDraftsApi.create(input)
          : await projectDraftsApi.update(draft.id, { ...input, revision: draft.revision });
      setDraft(value);
      setResumeId(value.id);
      setStep(steps.length - 1);
    } catch (caught) {
      setError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };
  const apply = async () => {
    if (draft === undefined) return;
    setBusy(true);
    setError(undefined);
    try {
      setDraft(await projectDraftsApi.apply(draft.id, draft.revision));
    } catch (caught) {
      setError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="project-wizard-heading" className="card stack">
      <div>
        <p className="eyebrow">Tek proje sihirbazı</p>
        <h2 id="project-wizard-heading">Güvenli proje kurulumu</h2>
        <p className="muted">
          Taslak sunucuda saklanır; sayfa yenilendiğinde taslak kimliğiyle devam edilir. Gizli
          değerler yalnızca yazılır, tekrar gösterilmez.
        </p>
      </div>
      <ol aria-label="Kurulum adımları" className="stepper">
        {steps.map((label, index) => (
          <li aria-current={index === step ? "step" : undefined} key={label}>
            {index + 1}. {label}
          </li>
        ))}
      </ol>
      {error === undefined ? null : (
        <AlertBanner heading="Taslak kaydedilemedi" tone="error">
          <p>{error}</p>
        </AlertBanner>
      )}
      <form className="form-stack" onSubmit={(event) => void save(event)}>
        <TextField
          label="Mevcut taslak kimliği"
          help="Sadece sunucudaki taslağı açmak için kullanılır; bu kimlik tek başına yetki vermez."
          onChange={(event) => setResumeId(event.currentTarget.value)}
          value={resumeId}
        />
        <Button busy={busy} onClick={() => void resume()} variant="secondary">
          Taslağı yükle
        </Button>
        <TextField
          label="Proje kimliği"
          onChange={(event) => setProjectId(event.currentTarget.value)}
          required
          value={projectId}
        />
        <TextField
          label="Kaynak bağlantısı kimliği"
          onChange={(event) => setSourceConnectionId(event.currentTarget.value)}
          required
          value={sourceConnectionId}
        />
        <SelectField
          label="Sunucunun değerlendireceği framework seçimi"
          onChange={(event) =>
            setFramework(event.currentTarget.value as ProjectDraftInput["framework"])
          }
          value={framework}
        >
          <option value="node">Node.js</option>
          <option value="nextjs">Next.js</option>
          <option value="static">Statik site</option>
        </SelectField>
        <fieldset>
          <legend>Çalışma zamanı</legend>
          <label>
            <input
              checked={runtime === "docker"}
              name="runtime"
              onChange={() => setRuntime("docker")}
              type="radio"
            />{" "}
            İzole Docker (varsayılan)
          </label>
          <label>
            <input
              checked={runtime === "native"}
              name="runtime"
              onChange={() => setRuntime("native")}
              type="radio"
            />{" "}
            Güvenilen yerel çalışma zamanı
          </label>
        </fieldset>
        {runtime === "native" ? (
          <Checkbox
            checked={trustedNative}
            label="Yerel çalışma zamanının daha geniş güven etkisini anladım ve açıkça onaylıyorum."
            onChange={(event) => setTrustedNative(event.currentTarget.checked)}
          />
        ) : null}
        <TextField
          label="Sağlık denetimi yolu"
          onChange={(event) => setHealthPath(event.currentTarget.value)}
          required
          value={healthPath}
        />
        <p className="muted">
          Ağ çıkışı sunucuda engellidir; tarayıcı bunu genişletemez. CPU, bellek ve disk güvenli
          başlangıç sınırlarında kalır.
        </p>
        <TextField
          help="Yalnızca ad sunucuya geri döner."
          label="Gizli değer adı (isteğe bağlı)"
          onChange={(event) => setEnvironmentName(event.currentTarget.value)}
          value={environmentName}
        />
        <TextField
          label="Gizli değer (yalnızca yazma)"
          onChange={(event) => setEnvironmentValue(event.currentTarget.value)}
          type="password"
          value={environmentValue}
        />
        <TextField
          help="Yalnızca Phase 4 tarafından doğrulanmış veritabanı kimliği kabul edilir."
          label="Veritabanı kimliği (isteğe bağlı)"
          onChange={(event) => setDatabaseId(event.currentTarget.value)}
          value={databaseId}
        />
        <TextField
          help="Yalnızca Phase 4 authoritative alan adı kimliği kabul edilir."
          label="Alan adı kimliği (isteğe bağlı)"
          onChange={(event) => setDomainId(event.currentTarget.value)}
          value={domainId}
        />
        <Button busy={busy} busyLabel="Sunucu taslağı kaydediyor…" type="submit">
          Güvenli taslağı kaydet ve incele
        </Button>
      </form>
      {draft === undefined ? null : (
        <>
          <InlineResult requestId={draft.id} tone="info">
            Taslak sürümü {draft.revision} sunucuda kaydedildi.
          </InlineResult>
          <WizardReview
            busy={busy}
            draft={draft}
            {...(error === undefined ? {} : { error })}
            onApply={() => void apply()}
          />
        </>
      )}
    </section>
  );
}
